<script>
// Gives the AI chat a generate_custom_widget tool on customWidgetAi entries. The chat
// collects the editor's request; the widget itself is generated by JAMES, so the
// generation instructions stay on the server. Renders nothing - it only registers the
// tool while the entry is open, like the entry toolset in extensions/ai/Sidebar.vue.
import { getValidToken } from '@/EntryApi'
import { store } from '@/ai/store'
import { registerTools, unregisterTools } from '@/ai/toolRegistry'
import { claimFields } from '@/ai/tools/entry'
import { aiSelector } from '../ai/selector'
import { generateIntoEntry, jamesOrigin } from './generate'

export const extension = {
	...aiSelector,
	type: 'EntryEditor',
	location: 'sidebar',
	contentTypes: [ 'customWidgetAi' ],
}

const NAME = 'generate_custom_widget'

// Runs fn with a signal that aborts as soon as one of the signals does. The listeners
// are removed afterwards - the editor's own signal lives as long as the editor.
async function withAnySignal(signals, fn) {
	const controller = new AbortController()
	const listeners = signals.map(signal => {
		const abort = () => controller.abort(signal.reason)
		if (signal.aborted) abort()
		else signal.addEventListener('abort', abort, { once: true })
		return () => signal.removeEventListener('abort', abort)
	})
	try {
		return await fn(controller.signal)
	}
	finally {
		listeners.forEach(remove => remove())
	}
}

export default {
	name: 'CustomWidgetAiChatTool',
	props: { ctx: Object },
	data: () => ({ toolsToken: null, releaseFields: null, closed: new AbortController() }),
	mounted() {
		// only this tool writes them, and the widget code never reaches the chat
		this.releaseFields = claimFields(this.ctx.state.entryApi, [ 'customCode', 'translations', 'conversationHistory' ], NAME)
		this.toolsToken = registerTools([ {
			name: NAME,
			definition: {
				name: NAME,
				description:
					'Create or change the widget of the open customWidgetAi entry. JAMES generates the HTML/CSS/JS ' +
					'template and its translations for all entry locales server-side and the tool writes customCode, ' +
					'translations and conversationHistory (autosaves). Pass the user\'s request verbatim as `request` - ' +
					'do not rephrase, shorten or add design details. If the entry already has a widget, the request is ' +
					'applied to it as a change. Set start_from_scratch only when the user explicitly asks for a ' +
					'completely new widget.',
				input_schema: {
					type: 'object',
					properties: {
						request: { type: 'string', description: 'The user\'s request, verbatim.' },
						start_from_scratch: { type: 'boolean', description: 'Discard the current widget and its history.' },
					},
					required: [ 'request' ],
				},
			},
			handler: async (input, { signal }) => {
				const request = String(input.request ?? '').trim()
				if (!request) throw new Error('request is empty.')

				const previews = (await this.$httpGet(this.ctx.endpoint + '/preview_environments')).items ?? []
				const origin = jamesOrigin(previews, localStorage.selectedPreview, localStorage.previewBaseUrlOverride)
				if (!origin) throw new Error('No preview environment with a JAMES URL is configured for this space.')
				const token = await getValidToken()
				if (!token) throw new Error('Not logged in to Contenthub - reload the page and log in again.')

				// Stop in the chat, or closing this editor, cancels the request to JAMES
				// and keeps the entry unchanged
				return withAnySignal([ signal, this.closed.signal ], signal => generateIntoEntry(request, !!input.start_from_scratch, {
					entryApi: this.ctx.state.entryApi,
					spaceId: this.ctx.spaceId,
					origin,
					token,
					// sent along when saved in the AI settings; otherwise JAMES uses its server key
					apiKey: store.config.apiKeys.anthropic,
					signal,
				}))
			},
		} ], this.ctx.state.entryApi)
	},
	beforeDestroy() {
		if (this.toolsToken != null) unregisterTools(this.toolsToken)
		this.releaseFields?.()
		this.closed.abort()
	},
	render: () => null,
}
</script>
